import * as z from 'zod';
import {
  compareDecimals,
  countDecimalPlaces,
  DECIMAL_MAX_PLACES,
  decimalFromStored,
  decimalPlacesMessage,
  formatPtBrDecimal,
  INTEGER_MESSAGE,
  maskOptionsFrom,
  numberRangeMessage,
  parsePtBrDecimal,
  type DecimalTypingOptions,
  type NumberMaskOptions,
  type NumberType,
} from '@record-fill';
import { FORM_FIELD_CHARACTER_LIMITS } from './form-field-character-limits';

export const MIN_GREATER_THAN_MAX_MESSAGE = 'O valor mínimo não pode ser maior que o valor máximo.';
export const DECIMAL_PLACES_LIMIT_MESSAGE = `A quantidade de casas decimais deve ser de no máximo ${DECIMAL_MAX_PLACES}.`;

// The panel edits the pt-BR text the author reads ('1.250,5'); the element stores the canonical
// decimal string ('1250.5') that the engine compares.
export type NumberInputFormValues = {
  name: string;
  value?: string;
  placeholder?: string;
  label?: string;
  labelPosition?: 'top' | 'left';
  required?: boolean;
  numberType?: NumberType;
  decimalPlaces?: string;
  hasMinValue?: boolean;
  hasMaxValue?: boolean;
  minValue?: string;
  maxValue?: string;
};

const NUMBER_FIELDS = ['value', 'minValue', 'maxValue'] as const;
type NumberField = (typeof NUMBER_FIELDS)[number];

export function toNumberDisplayText(stored: string | undefined): string {
  const canonical = decimalFromStored(stored);
  return canonical === null ? (stored ?? '') : formatPtBrDecimal(canonical);
}

export function toCanonicalNumber(text: string | undefined): string | undefined {
  return text ? (parsePtBrDecimal(text) ?? undefined) : undefined;
}

export function toDecimalPlaces(text: string | undefined): number | undefined {
  return text ? Number(text) : undefined;
}

export function numberTypingFrom(form: NumberInputFormValues): DecimalTypingOptions {
  return { numberType: form.numberType, decimalPlaces: toDecimalPlaces(form.decimalPlaces) };
}

// The mask of "Valor" follows the limits as the author types them; a limit that is switched off or
// not yet a number caps nothing.
export function valueMaskFrom(form: NumberInputFormValues): NumberMaskOptions {
  return maskOptionsFrom({
    ...numberTypingFrom(form),
    minValue: form.hasMinValue ? toCanonicalNumber(form.minValue) : undefined,
    maxValue: form.hasMaxValue ? toCanonicalNumber(form.maxValue) : undefined,
  });
}

function allowedDecimalPlaces(form: NumberInputFormValues): number | undefined {
  if (form.numberType === 'integer') return 0;
  const places = toDecimalPlaces(form.decimalPlaces);
  return form.numberType === 'decimal' && places !== undefined && places <= DECIMAL_MAX_PLACES ? places : undefined;
}

function checkDecimalPlaces(form: NumberInputFormValues, ctx: z.RefinementCtx): void {
  if (form.numberType !== 'decimal') return;
  const places = toDecimalPlaces(form.decimalPlaces);
  if (places === undefined) {
    ctx.addIssue({ code: 'custom', path: ['decimalPlaces'], message: 'Digite um valor' });
  } else if (places > DECIMAL_MAX_PLACES) {
    ctx.addIssue({ code: 'custom', path: ['decimalPlaces'], message: DECIMAL_PLACES_LIMIT_MESSAGE });
  }
}

function isActiveField(form: NumberInputFormValues, field: NumberField): boolean {
  if (field === 'minValue') return form.hasMinValue === true;
  if (field === 'maxValue') return form.hasMaxValue === true;
  return true;
}

function fieldIssue(decimal: string | null, allowedPlaces: number | undefined): string | null {
  if (decimal === null) return 'Informe um número.';
  if (allowedPlaces === undefined || countDecimalPlaces(decimal) <= allowedPlaces) return null;
  return allowedPlaces === 0 ? INTEGER_MESSAGE : decimalPlacesMessage(allowedPlaces);
}

// Reports each unreadable or over-precise field and returns the canonical value of the others,
// which are the only ones the range checks may compare.
function readNumberFields(
  form: NumberInputFormValues,
  ctx: z.RefinementCtx
): Partial<Record<NumberField, string>> {
  const allowedPlaces = allowedDecimalPlaces(form);
  const canonical: Partial<Record<NumberField, string>> = {};
  for (const field of NUMBER_FIELDS) {
    const text = form[field];
    if (!isActiveField(form, field) || !text) continue;
    const decimal = parsePtBrDecimal(text);
    const message = fieldIssue(decimal, allowedPlaces);
    if (message) ctx.addIssue({ code: 'custom', path: [field], message });
    else if (decimal !== null) canonical[field] = decimal;
  }
  return canonical;
}

function checkRange(canonical: Partial<Record<NumberField, string>>, ctx: z.RefinementCtx): void {
  const { value, minValue, maxValue } = canonical;
  if (minValue !== undefined && maxValue !== undefined && compareDecimals(minValue, maxValue) > 0) {
    ctx.addIssue({ code: 'custom', path: ['minValue'], message: MIN_GREATER_THAN_MAX_MESSAGE });
    return;
  }
  if (value === undefined) return;
  const belowMin = minValue !== undefined && compareDecimals(value, minValue) < 0;
  const aboveMax = maxValue !== undefined && compareDecimals(value, maxValue) > 0;
  if (belowMin || aboveMax) {
    ctx.addIssue({ code: 'custom', path: ['value'], message: numberRangeMessage(minValue, maxValue) });
  }
}

function validateNumberInputForm(form: NumberInputFormValues, ctx: z.RefinementCtx): void {
  checkDecimalPlaces(form, ctx);
  checkRange(readNumberFields(form, ctx), ctx);
}

export const numberInputPropertiesSchema = z
  .object({
    name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
    value: z.string().max(FORM_FIELD_CHARACTER_LIMITS.VALUE).optional(),
    placeholder: z.string().max(FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT).optional(),
    label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
    labelPosition: z.enum(['top', 'left']).optional(),
    required: z.boolean().optional(),
    numberType: z.enum(['integer', 'decimal']).optional(),
    decimalPlaces: z.string().regex(/^\d*$/, 'Digite um número inteiro').optional(),
    hasMinValue: z.boolean().optional(),
    hasMaxValue: z.boolean().optional(),
    minValue: z.string().optional(),
    maxValue: z.string().optional(),
  })
  .superRefine(validateNumberInputForm);
